import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';
import { ESG_CONTENT_SHAPE } from '../../constants/colors';
import { contentBorders } from '../../theme/createContentTheme';

export interface ChartPanelProps {
  title: React.ReactNode;
  icon?: SvgIconComponent;
  /** One-line caption under the title (unit, basis, period). */
  caption?: React.ReactNode;
  /** Controls at the end of the title row (e.g. chart-type menu). */
  actions?: React.ReactNode;
  /** Footnote / methodology line under the chart. */
  footer?: React.ReactNode;
  children: React.ReactNode;
  id?: string;
  sx?: SxProps<Theme>;
}

/** Outlined panel shared by every chart / breakdown on the content canvas. */
const ChartPanel: React.FC<ChartPanelProps> = ({ title, icon: Icon, caption, actions, footer, children, id, sx }) => (
  <Paper
    id={id}
    variant="outlined"
    sx={[
      {
        p: { xs: 2, md: 2.5 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: `${ESG_CONTENT_SHAPE.md}px`,
        bgcolor: 'background.paper',
        minWidth: 0,
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {Icon ? <Icon aria-hidden sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} /> : null}
          <Typography variant="subtitle2" component="h3" sx={{ m: 0, color: 'text.primary' }}>
            {title}
          </Typography>
        </Box>
        {caption ? (
          <Typography variant="caption" component="p" sx={{ mt: 0.25, color: 'text.secondary' }}>
            {caption}
          </Typography>
        ) : null}
      </Box>
      {actions ? <Box sx={{ flexShrink: 0, mt: -0.75, mx: -0.75 }}>{actions}</Box> : null}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    {footer ? (
      <Box
        sx={(theme) => ({
          mt: 2,
          pt: 1.5,
          borderTop: `1px solid ${contentBorders(theme).subtle}`,
        })}
      >
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
          {footer}
        </Typography>
      </Box>
    ) : null}
  </Paper>
);

export default ChartPanel;
